import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { NotificationTemplatePreview as PreviewData } from '@/types/notificationTemplate.types';

interface TemplatePreviewProps {
  preview: PreviewData | null;
  isLoading: boolean;
  onRefresh: () => void;
}

export function TemplatePreview({ preview, isLoading, onRefresh }: TemplatePreviewProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'html' | 'text'>('html');

  const iframeSrcDoc = useMemo(() => {
    if (!preview?.html) return '';
    return preview.html;
  }, [preview?.html]);

  return (
    <div className='flex flex-col h-full'>
      <div className='flex items-center justify-between mb-2'>
        <div className='flex gap-1'>
          <button
            onClick={() => setTab('html')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded',
              tab === 'html' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-700',
            )}>
            {t('notificationTemplates.editor.previewHtml')}
          </button>
          <button
            onClick={() => setTab('text')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded',
              tab === 'text' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-700',
            )}>
            {t('notificationTemplates.editor.previewText')}
          </button>
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className='px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50'>
          {isLoading ? t('common.loading') : t('notificationTemplates.editor.refreshPreview')}
        </button>
      </div>

      <div className='flex-1 rounded border border-gray-200 bg-white overflow-hidden min-h-[300px]'>
        {!preview && (
          <div className='flex items-center justify-center h-full text-sm text-gray-400'>
            {t('notificationTemplates.editor.previewEmpty')}
          </div>
        )}
        {preview && tab === 'html' && (
          <iframe
            srcDoc={iframeSrcDoc}
            title='Template Preview'
            className='w-full h-full border-0'
            sandbox='allow-same-origin'
          />
        )}
        {preview && tab === 'text' && (
          <pre className='p-4 text-sm text-gray-700 whitespace-pre-wrap font-mono overflow-auto h-full'>
            {preview.text}
          </pre>
        )}
      </div>
    </div>
  );
}
